/*
 * pes-quote-pages.js — PES quote list + detail pages (P1 quote program).
 *
 * Powers /pages/quotes (list + detail via ?id=<ref>). Talks to the same
 * app-proxy backend as the drawer:
 *   GET    {ep}?email=…                  list
 *   GET    {ep}/{ref}?email=…            detail (lines carry savings_pct,
 *                                        list_price, volume_progress)
 *   POST   {ep}                          create {email, name}
 *   POST   {ep}/{ref}/rename             {email, name}
 *   POST   {ep}/{ref}/lines              {email, action, sku, qty}
 *   POST   {ep}/{ref}/convert            -> {permalink, drift, requires_review}
 *   POST   {ep}/{ref}/expire-refresh     re-quote expired quote
 *   POST   {ep}/{ref}/delete             {email, confirm:true} — CANNOT UNDO
 *   POST   {ep}/{ref}/share              {email, mode, note?} -> {token, share_path}
 *   POST   {ep}/{ref}/share/revoke       {email, token?}
 *
 * Owner-documented behaviors (deep-pass corrections, binding):
 *   - edit RESTATES validity +7d (ours) vs Lowe's cancel-and-regenerate —
 *     UI warns "changes can affect pricing", no fake "new quote" churn
 *   - volume progress bar is DISPLAY ONLY and is suppressed on quotes with
 *     contract/pricelist pricing (non-stacking rule; backend decides)
 *   - delete has no undo — confirm dialog says so verbatim
 *   - customer-facing copy never mentions suppliers/dropship; quotes never
 *     promise stock — footer on every view
 */

(function () {
  'use strict';

  var EMAIL_KEY = 'pesq_email';

  function money(n) {
    var v = Number(n);
    if (!isFinite(v)) return '$0.00';
    return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function expiryLabel(days, expired) {
    if (expired) return 'Expired';
    if (days === null || days === undefined) return '';
    if (days <= 0) return 'Expires today';
    if (days === 1) return 'Expires in 1 day';
    return 'Expires in ' + days + ' days';
  }

  function dateOnly(iso) {
    return iso ? String(iso).slice(0, 10) : '';
  }

  class PesQuotePages extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.getAttribute('data-endpoint') || '/apps/quotes';
      this.customerEmail = this.getAttribute('data-customer-email') || null;
      this.customerId = this.getAttribute('data-customer-id') || null;
      this.quotes = [];
      this.filter = { q: '', status: 'active', sort: 'expiry' };
      this.build();
      if (!this.email()) return this.renderEmailCapture();
      var ref = new URLSearchParams(window.location.search).get('id');
      if (ref) this.renderDetail(ref); else this.renderList();
    }

    build() {
      this.innerHTML = '<div class="pesqp-wrap" data-pesqp-root></div>';
      this.root = this.querySelector('[data-pesqp-root]');
    }

    email() {
      return this.customerEmail || localStorage.getItem(EMAIL_KEY) || null;
    }

    api(method, path, body) {
      return fetch(this.endpoint + path, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) { var err = new Error(j.error || 'Request failed'); err.degraded = r.status >= 500; throw err; }
          return j;
        });
      });
    }

    footer(html) {
      return '<p class="pesqp-footer">' + esc(html ||
        'Prices held until the expiry date on eligible items. Availability confirmed at order time.') + '</p>';
    }

    /* ---------------- email capture (guest identity, P0 pattern) ---------------- */

    renderEmailCapture() {
      var self = this;
      this.root.innerHTML =
        '<div class="pesqp-head"><h1 class="pesqp-title">My Quotes</h1></div>' +
        '<p class="pesqp-muted">Enter your email to see your saved quotes.</p>' +
        '<form class="pesqp-email-form">' +
          '<input class="field__input" type="email" required placeholder="you@company.com" aria-label="Email" style="max-width:360px;display:block;margin-bottom:12px">' +
          '<button type="submit" class="button button--primary">Continue</button>' +
        '</form>' +
        '<p class="pesqp-error" hidden></p>';
      this.root.querySelector('form').addEventListener('submit', function (e) {
        e.preventDefault();
        var v = this.querySelector('input').value.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
          self.root.querySelector('.pesqp-error').hidden = false;
          self.root.querySelector('.pesqp-error').textContent = 'Please enter a valid email.';
          return;
        }
        localStorage.setItem(EMAIL_KEY, v);
        self.renderList();
      });
    }

    /* ---------------- list view ---------------- */

    renderList() {
      var self = this;
      this.root.innerHTML =
        '<div class="pesqp-head"><h1 class="pesqp-title">My Quotes</h1></div>' +
        '<p class="pesqp-loading">Loading your quotes&hellip;</p>';
      this.api('GET', '?email=' + encodeURIComponent(this.email()))
        .then(function (data) { self.quotes = data.quotes || []; self.paintList(); })
        .catch(function (e) { self.renderDegraded(e); });
    }

    filteredQuotes() {
      var f = this.filter;
      var out = this.quotes.filter(function (q) {
        if (f.status === 'active' && q.expired) return false;
        if (f.status === 'expired' && !q.expired) return false;
        if (f.q) {
          var hay = (q.name + ' ' + q.quote_no).toLowerCase();
          if (hay.indexOf(f.q.toLowerCase()) === -1) return false;
        }
        return true;
      });
      out.sort(function (a, b) {
        if (f.sort === 'expiry') return String(a.expiry || '9999').localeCompare(String(b.expiry || '9999'));
        if (f.sort === 'total') return (b.total || 0) - (a.total || 0);
        return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
      });
      return out;
    }

    paintList() {
      var self = this;
      var cards = this.filteredQuotes().map(function (q) {
        var badge = q.expired
          ? '<span class="pesqp-badge pesqp-badge--expired">Expired</span>'
          : '<span class="pesqp-badge pesqp-badge--active">Active</span>';
        return (
          '<a class="pesqp-card' + (q.expired ? ' pesqp-card--expired' : '') + '" href="?id=' + encodeURIComponent(q.id) + '" data-ref="' + q.id + '">' +
            '<span class="pesqp-card__name">' + esc(q.name) + badge + '</span>' +
            '<span class="pesqp-card__meta">' + esc(q.quote_no) + ' &middot; Est. total ' + money(q.total) +
              ' &middot; ' + q.line_count + ' item' + (q.line_count === 1 ? '' : 's') + '</span>' +
            '<span class="pesqp-card__dates">' + esc(expiryLabel(q.expires_in_days, q.expired)) +
              ' &middot; Updated ' + esc(dateOnly(q.updated_at)) + '</span>' +
          '</a>'
        );
      }).join('');

      this.root.innerHTML =
        '<div class="pesqp-head">' +
          '<h1 class="pesqp-title">My Quotes</h1>' +
          '<button type="button" class="button button--secondary" data-pesqp-new>New Quote</button>' +
        '</div>' +
        '<div class="pesqp-toolbar">' +
          '<input class="field__input pesqp-search" type="search" placeholder="Search by quote name or #" aria-label="Search quotes" value="' + esc(this.filter.q) + '">' +
          '<select class="pesqp-select" data-pesqp-status aria-label="Status filter">' +
            '<option value="active"' + (this.filter.status === 'active' ? ' selected' : '') + '>Active</option>' +
            '<option value="expired"' + (this.filter.status === 'expired' ? ' selected' : '') + '>Expired</option>' +
            '<option value="all"' + (this.filter.status === 'all' ? ' selected' : '') + '>All</option>' +
          '</select>' +
          '<select class="pesqp-select" data-pesqp-sort aria-label="Sort">' +
            '<option value="expiry"' + (this.filter.sort === 'expiry' ? ' selected' : '') + '>Expiry soonest</option>' +
            '<option value="updated"' + (this.filter.sort === 'updated' ? ' selected' : '') + '>Recently updated</option>' +
            '<option value="total"' + (this.filter.sort === 'total' ? ' selected' : '') + '>Highest total</option>' +
          '</select>' +
        '</div>' +
        '<div class="pesqp-cards">' +
          (cards || '<p class="pesqp-empty">No quotes match. Create one from any product page with &ldquo;Add to Quote&rdquo;.</p>') +
        '</div>' +
        '<div class="pesqp-reorder">' +
          '<h2 class="pesqp-reorder__title">Reorder a past job</h2>' +
          '<p class="pesqp-muted">Search your converted quotes and past orders by quote name, PO / job name, or order #. ' +
            'When every item is still available, the job is rebuilt as a new quote at the original prices. ' +
            'If something changed, you get a cart at current prices with a clear notice &mdash; never a silent reprice.</p>' +
          '<div class="pesqp-reorder__bar">' +
            '<input class="field__input" type="search" placeholder="Search past jobs&hellip;" data-pesqp-reorder-q aria-label="Search past jobs">' +
            '<button type="button" class="button button--secondary" data-pesqp-reorder-go>Search</button>' +
          '</div>' +
          '<div data-pesqp-reorder-out><p class="pesqp-muted">Loading past jobs&hellip;</p></div>' +
        '</div>' +
        this.footer('Prices held for 7 days on eligible items. Availability confirmed at order time.');

      var search = this.root.querySelector('.pesqp-search');
      search.addEventListener('input', function () {
        self.filter.q = this.value;
        self.paintList();
        var s = self.root.querySelector('.pesqp-search');
        s.focus();
        s.setSelectionRange(s.value.length, s.value.length);
      });
      this.root.querySelector('[data-pesqp-status]').addEventListener('change', function () {
        self.filter.status = this.value;
        self.paintList();
      });
      this.root.querySelector('[data-pesqp-sort]').addEventListener('change', function () {
        self.filter.sort = this.value;
        self.paintList();
      });
      this.root.querySelector('[data-pesqp-new]').addEventListener('click', function () { self.promptNewQuote(); });
      this.bindReorder();
    }

    promptNewQuote() {
      var self = this;
      var name = window.prompt('Name your new quote (e.g. Fence Job):');
      if (!name || !name.trim()) return;
      this.classList.add('pesqp-busy');
      this.api('POST', '', { email: this.email(), customer_id: this.customerId, name: name.trim() })
        .then(function (out) {
          self.classList.remove('pesqp-busy');
          self.renderDetail(out.quote.id);
        })
        .catch(function (e) {
          self.classList.remove('pesqp-busy');
          window.alert(e.message);
        });
    }

    /* ---------------- detail view ---------------- */

    renderDetail(ref) {
      var self = this;
      this.root.innerHTML = '<p class="pesqp-loading">Loading quote&hellip;</p>';
      this.api('GET', '/' + encodeURIComponent(ref) + '?email=' + encodeURIComponent(this.email()))
        .then(function (quote) { self.paintDetail(quote); })
        .catch(function (e) { self.renderDegraded(e); });
    }

    paintDetail(q) {
      var self = this;
      var readOnly = !!q.expired;

      var banner;
      if (q.expired) {
        banner = '<div class="pesqp-banner pesqp-banner--expired">This quote expired on ' + esc(q.expiry) +
          '. It&rsquo;s read-only &mdash; request a re-quote to refresh prices and validity.</div>';
      } else if (q.expires_in_days != null && q.expires_in_days <= 2) {
        banner = '<div class="pesqp-banner pesqp-banner--warn">' + esc(expiryLabel(q.expires_in_days, false)) +
          ' (' + esc(q.expiry) + '). Making changes can affect pricing and restarts the 7-day hold.</div>';
      } else {
        banner = '<div class="pesqp-banner pesqp-banner--ok">' + esc(expiryLabel(q.expires_in_days, false)) +
          ' (' + esc(q.expiry) + '). Making changes can affect pricing and restarts the 7-day hold.</div>';
      }

      // Wave-2B (owner ruling 2026-10-08): flagged quotes convert freely —
      // the flag is staff-facing only. Customer sees a soft, warm note.
      var flag = q.flag;
      if (flag && flag.required) {
        banner += '<div class="pesqp-banner pesqp-banner--approved">Our team is taking a closer look at this quote and may reach out to help with the details &mdash; you can check out anytime, nothing is on hold.</div>';
      }

      // Volume progress bar — backend suppresses it on contract-priced quotes
      // (non-stacking rule) and when the threshold/pricelist is unknown.
      var progressHtml = '';
      var vp = q.volume_progress;
      if (vp && vp.eligible) {
        var label = vp.reached
          ? 'This quote is over the ' + money(vp.threshold) + ' volume threshold. Volume pricing, if applicable, is confirmed when the quote is finalized.'
          : 'You&rsquo;re ' + money(vp.remaining) + ' away from volume pricing.';
        progressHtml =
          '<div class="pesqp-progress">' +
            '<p class="pesqp-progress__label">' + label + '</p>' +
            '<div class="pesqp-progress__track"><div class="pesqp-progress__fill" style="width:' + vp.pct + '%"></div></div>' +
            '<div class="pesqp-progress__scale"><span>' + money(vp.current) + '</span><span>' + money(vp.threshold) + '</span></div>' +
          '</div>';
      }

      var rows = (q.lines || []).map(function (l) {
        var savings = l.savings_pct != null
          ? '<span class="pesqp-save-badge">Save ' + l.savings_pct + '%</span>' : '';
        var list = (l.list_price != null && l.unit_price < l.list_price)
          ? '<span class="pesqp-line__list">' + money(l.list_price) + '</span>' : '';
        // Wave-2B (#109): the customer's own part number rides alongside our SKU.
        var skuLine = l.sku
          ? 'SKU ' + esc(l.sku) + (l.customer_sku ? ' &middot; Your # ' + esc(l.customer_sku) : '')
          : (l.customer_sku ? 'Your # ' + esc(l.customer_sku) : '');
        var qtyCell = readOnly
          ? String(l.qty)
          : '<span class="pesqp-qty">' +
              '<button type="button" data-pesqp-dec="' + l.line_id + '" aria-label="Decrease">&minus;</button>' +
              '<input type="number" min="1" value="' + l.qty + '" data-pesqp-qty="' + l.line_id + '" data-sku="' + esc(l.sku || '') + '" aria-label="Quantity">' +
              '<button type="button" data-pesqp-inc="' + l.line_id + '" aria-label="Increase">+</button>' +
            '</span>';
        var removeCell = readOnly ? '' :
          '<button type="button" class="pesqp-remove" data-pesqp-remove="' + l.line_id + '" data-sku="' + esc(l.sku || '') + '">Remove</button>';
        return (
          '<tr>' +
            '<td><span class="pesqp-line__title">' + esc(l.title) + '</span>' +
              (skuLine ? '<span class="pesqp-line__sku">' + skuLine + '</span>' : '') + '</td>' +
            '<td>' + qtyCell + '</td>' +
            '<td>' + money(l.unit_price) + list + savings + '</td>' +
            '<td>' + money(l.line_total) + '</td>' +
            '<td>' + removeCell + '</td>' +
          '</tr>'
        );
      }).join('');

      var actions = readOnly
        ? '<button type="button" class="button button--primary" data-pesqp-requote>Request Re-quote</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-copy>Make a Copy</button>' +
          '<a class="button button--secondary" href="' + this.endpoint + '/' + encodeURIComponent(q.id) + '/pdf?email=' + encodeURIComponent(this.email()) + '">Download PDF</a>' +
          '<button type="button" class="button button--secondary" data-pesqp-back>Back to My Quotes</button>'
        : '<button type="button" class="button button--primary" data-pesqp-convert>Start Secure Checkout</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-share>Share Quote</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-copy>Make a Copy</button>' +
          '<a class="button button--secondary" href="' + this.endpoint + '/' + encodeURIComponent(q.id) + '/pdf?email=' + encodeURIComponent(this.email()) + '">Download PDF</a>' +
          '<button type="button" class="button button--secondary" data-pesqp-rename>Rename</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-delete>Delete</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-back>Back</button>';

      var quickAdd = readOnly ? '' :
        '<div class="pesqp-quickadd">' +
          '<div class="pesqp-typeahead">' +
            '<input class="field__input" type="text" placeholder="Quick add: search by SKU or product name" data-pesqp-sku aria-label="Add by SKU or name" autocomplete="off">' +
            '<div class="pesqp-typeahead__list" data-pesqp-suggest hidden></div>' +
          '</div>' +
          '<input class="field__input" type="number" min="1" value="1" data-pesqp-addqty aria-label="Quantity">' +
          '<button type="button" class="button button--secondary" data-pesqp-add>Add Item</button>' +
          '<button type="button" class="button button--secondary" data-pesqp-bulk-open>Bulk Add (Paste List)</button>' +
          '<button type="button" class="pesqp-linklike" data-pesqp-aliases-open>Your Part Numbers</button>' +
        '</div>' +
        '<div class="pesqp-bulk" data-pesqp-bulk hidden>' +
          '<p class="pesqp-muted">Paste one line per item &mdash; <strong>SKU, qty</strong> (CSV, tab, or spaces all work). ' +
            'Your own part numbers work too once saved under Your Part Numbers. ' +
            'Nothing is added until you confirm; every line that can&rsquo;t be resolved is listed with its reason.</p>' +
          '<textarea class="field__input pesqp-bulk__input" rows="6" data-pesqp-bulk-text placeholder="USS-MIDN-LBBC, 4&#10;RUN-400W-M, 2" aria-label="Paste SKU list"></textarea>' +
          '<div class="pesqp-bulk__actions">' +
            '<button type="button" class="button button--secondary" data-pesqp-bulk-preview>Preview</button>' +
            '<button type="button" class="button button--primary" data-pesqp-bulk-confirm disabled>Add Items</button>' +
            '<button type="button" class="button button--secondary" data-pesqp-bulk-cancel>Cancel</button>' +
          '</div>' +
          '<div data-pesqp-bulk-result></div>' +
        '</div>' +
        // Wave-2B (#109): customer part-number alias manager — self-service.
        '<div class="pesqp-aliases" data-pesqp-aliases hidden>' +
          '<h3 class="pesqp-aliases__title">Your Part Numbers</h3>' +
          '<p class="pesqp-muted">Save your own part numbers and we&rsquo;ll match them automatically in Bulk Add and show them on your quotes and PDFs.</p>' +
          '<div data-pesqp-alias-list><p class="pesqp-muted">Loading&hellip;</p></div>' +
          '<div class="pesqp-aliases__add">' +
            '<input class="field__input" type="text" maxlength="64" placeholder="Your part # (e.g. ACME-123)" data-pesqp-alias-csku aria-label="Your part number">' +
            '<input class="field__input" type="text" maxlength="64" placeholder="Our SKU (e.g. USS-MIDN-LBBC)" data-pesqp-alias-osku aria-label="Our SKU">' +
            '<button type="button" class="button button--secondary" data-pesqp-alias-save>Save</button>' +
          '</div>' +
        '</div>';

      this.root.innerHTML =
        '<div class="pesqp-head">' +
          '<h1 class="pesqp-title">' + esc(q.name) + '</h1>' +
          '<span class="pesqp-muted">' + esc(q.quote_no) + ' &middot; Updated ' + esc(dateOnly(q.updated_at)) + '</span>' +
        '</div>' +
        banner +
        progressHtml +
        '<table class="pesqp-table"><thead><tr><th>Item</th><th>Qty</th><th>Unit price</th><th>Line total</th><th></th></tr></thead>' +
          '<tbody>' + (rows || '<tr><td colspan="5" class="pesqp-muted">No items yet — add by SKU below or from any product page.</td></tr>') + '</tbody></table>' +
        '<div class="pesqp-totalrow"><span>Estimated total</span><span>' + money(q.total) + '</span></div>' +
        '<p class="pesqp-pricing-link"><button type="button" class="pesqp-linklike" data-pesqp-pricing>Questions about pricing?</button></p>' +
        quickAdd +
        '<div class="pesqp-actions">' + actions + '</div>' +
        this.footer(q.footer);

      this.bindDetail(q, readOnly);
    }

    bindDetail(q, readOnly) {
      var self = this;
      var back = this.root.querySelector('[data-pesqp-back]');
      if (back) back.addEventListener('click', function () {
        window.history.replaceState(null, '', window.location.pathname);
        self.renderList();
      });

      // Make a Copy — available on active AND expired (read-only) quotes.
      // New quote #, fresh 7-day validity, carries lines + PO/Job + notes.
      var copyBtn = this.root.querySelector('[data-pesqp-copy]');
      if (copyBtn) copyBtn.addEventListener('click', function () { self.copyQuote(q); });

      // Pricing-education modal ("Questions about pricing?") — static content,
      // no backend call. Available on active and expired quotes alike.
      var pricingBtn = this.root.querySelector('[data-pesqp-pricing]');
      if (pricingBtn) pricingBtn.addEventListener('click', function () { self.openPricingDialog(q); });

      if (readOnly) {
        var rq = this.root.querySelector('[data-pesqp-requote]');
        if (rq) rq.addEventListener('click', function () {
          self.classList.add('pesqp-busy');
          self.api('POST', '/' + q.id + '/expire-refresh', { email: self.email() })
            .then(function (fresh) { self.classList.remove('pesqp-busy'); self.paintDetail(fresh); })
            .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
        });
        return;
      }

      // qty edits (update-in-place; restates validity +7d server-side)
      function setQty(lineId, sku, qty) {
        if (qty < 1) return;
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/lines', { email: self.email(), action: 'update', sku: sku, qty: qty })
          .then(function (fresh) { self.classList.remove('pesqp-busy'); self.paintDetail(fresh); })
          .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
      }
      this.root.querySelectorAll('[data-pesqp-inc]').forEach(function (b) {
        b.addEventListener('click', function () {
          var input = self.root.querySelector('[data-pesqp-qty="' + b.getAttribute('data-pesqp-inc') + '"]');
          setQty(null, input.getAttribute('data-sku'), parseInt(input.value, 10) + 1);
        });
      });
      this.root.querySelectorAll('[data-pesqp-dec]').forEach(function (b) {
        b.addEventListener('click', function () {
          var input = self.root.querySelector('[data-pesqp-qty="' + b.getAttribute('data-pesqp-dec') + '"]');
          setQty(null, input.getAttribute('data-sku'), parseInt(input.value, 10) - 1);
        });
      });
      this.root.querySelectorAll('[data-pesqp-qty]').forEach(function (input) {
        input.addEventListener('change', function () {
          setQty(null, input.getAttribute('data-sku'), parseInt(input.value, 10));
        });
      });
      this.root.querySelectorAll('[data-pesqp-remove]').forEach(function (b) {
        b.addEventListener('click', function () {
          self.classList.add('pesqp-busy');
          self.api('POST', '/' + q.id + '/lines', { email: self.email(), action: 'remove', sku: b.getAttribute('data-sku') })
            .then(function (fresh) { self.classList.remove('pesqp-busy'); self.paintDetail(fresh); })
            .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
        });
      });

      // Quick Add — typeahead over the 24k-entry catalog; add goes through
      // the bulk endpoint with a single line (same idempotent path).
      var skuInput = this.root.querySelector('[data-pesqp-sku]');
      var suggest = this.root.querySelector('[data-pesqp-suggest]');
      var debounce = null;
      function hideSuggest() { if (suggest) { suggest.hidden = true; suggest.innerHTML = ''; } }
      if (skuInput && suggest) {
        skuInput.addEventListener('input', function () {
          var term = skuInput.value.trim();
          clearTimeout(debounce);
          if (term.length < 2) return hideSuggest();
          debounce = setTimeout(function () {
            self.api('GET', '/sku-search?q=' + encodeURIComponent(term))
              .then(function (out) {
                var rows = (out.results || []).map(function (r) {
                  return '<button type="button" class="pesqp-typeahead__item" data-sku="' + esc(r.sku) + '">' +
                    '<span class="pesqp-typeahead__title">' + esc(r.title || r.sku) + '</span>' +
                    '<span class="pesqp-typeahead__meta">SKU ' + esc(r.sku) +
                      (r.shopify_price != null ? ' &middot; ' + money(r.shopify_price) : '') + '</span>' +
                  '</button>';
                }).join('');
                if (!rows) return hideSuggest();
                suggest.innerHTML = rows;
                suggest.hidden = false;
                suggest.querySelectorAll('[data-sku]').forEach(function (b) {
                  b.addEventListener('click', function () {
                    skuInput.value = b.getAttribute('data-sku');
                    hideSuggest();
                    skuInput.focus();
                  });
                });
              })
              .catch(hideSuggest);
          }, 300);
        });
        skuInput.addEventListener('blur', function () { setTimeout(hideSuggest, 200); });
      }

      var addBtn = this.root.querySelector('[data-pesqp-add]');
      if (addBtn) addBtn.addEventListener('click', function () {
        var sku = self.root.querySelector('[data-pesqp-sku]').value.trim();
        var qty = parseInt(self.root.querySelector('[data-pesqp-addqty]').value, 10) || 1;
        if (!sku) return window.alert('Enter a SKU first.');
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/lines/bulk', { email: self.email(), lines: [{ sku: sku, qty: qty }], confirm: true })
          .then(function (out) {
            self.classList.remove('pesqp-busy');
            if (out.failed_count > 0) {
              window.alert('Could not add ' + sku + ': ' + (out.failed[0] && out.failed[0].reason ? out.failed[0].reason : 'unknown reason'));
              self.paintDetail(out.quote);
              return;
            }
            self.paintDetail(out.quote);
          })
          .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
      });

      // Bulk Add (paste list) — preview table first, one confirm call adds all.
      var bulkWrap = this.root.querySelector('[data-pesqp-bulk]');
      var bulkText = this.root.querySelector('[data-pesqp-bulk-text]');
      var bulkResult = this.root.querySelector('[data-pesqp-bulk-result]');
      var bulkConfirm = this.root.querySelector('[data-pesqp-bulk-confirm]');
      this.root.querySelector('[data-pesqp-bulk-open]').addEventListener('click', function () {
        bulkWrap.hidden = !bulkWrap.hidden;
        if (!bulkWrap.hidden) bulkText.focus();
      });
      this.root.querySelector('[data-pesqp-bulk-cancel]').addEventListener('click', function () {
        bulkWrap.hidden = true;
        bulkText.value = '';
        bulkResult.innerHTML = '';
        bulkConfirm.disabled = true;
      });
      this.root.querySelector('[data-pesqp-bulk-preview]').addEventListener('click', function () {
        var text = bulkText.value;
        if (!text.trim()) return window.alert('Paste some lines first (SKU, qty per line).');
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/lines/bulk', { email: self.email(), text: text, confirm: false })
          .then(function (out) {
            self.classList.remove('pesqp-busy');
            var okRows = out.resolved.map(function (r) {
              // Wave-2B (#109): lines matched via a saved customer part number
              // are called out so the contractor sees THEIR numbering working.
              var viaAlias = r.via_alias
                ? '<span class="pesqp-badge pesqp-badge--alias">via your # ' + esc(r.customer_sku) + '</span>' : '';
              return '<tr><td>' + esc(r.title || r.sku) + '<span class="pesqp-line__sku">SKU ' + esc(r.sku) + '</span>' + viaAlias + '</td>' +
                '<td>' + esc(r.qty) + '</td>' +
                '<td><span class="pesqp-badge pesqp-badge--active">Ready</span></td></tr>';
            }).join('');
            // Wave-2B (#109): one click from a failed line saves the token as
            // the customer's part number for one of our SKUs.
            var badRows = out.failed.map(function (f, fi) {
              var mapper = f.sku
                ? '<div class="pesqp-alias-mapper">' +
                    '<input class="field__input" type="text" maxlength="64" placeholder="Our SKU" data-pesqp-map-sku="' + fi + '" aria-label="Our SKU for ' + esc(f.sku) + '">' +
                    '<button type="button" class="button button--secondary" data-pesqp-map-save="' + fi + '" data-csku="' + esc(f.sku) + '">Save &lsquo;' + esc(f.sku) + '&rsquo; as your part #</button>' +
                  '</div>'
                : '';
              return '<tr class="pesqp-bulk__bad"><td>Line ' + esc(f.line) + ': ' + esc(f.raw) + mapper + '</td>' +
                '<td>&mdash;</td>' +
                '<td><span class="pesqp-badge pesqp-badge--expired">' + esc(f.reason) + '</span></td></tr>';
            }).join('');
            bulkResult.innerHTML =
              '<table class="pesqp-table pesqp-bulk__table"><thead><tr><th>Item</th><th>Qty</th><th>Status</th></tr></thead>' +
              '<tbody>' + okRows + badRows + '</tbody></table>' +
              '<p class="pesqp-muted">' + out.resolved_count + ' line(s) ready to add' +
                (out.failed_count ? '; ' + out.failed_count + ' line(s) can&rsquo;t be resolved and will be skipped &mdash; they are listed above, never silently dropped.' : '.') + '</p>';
            bulkConfirm.disabled = out.resolved_count === 0;
            bulkConfirm.textContent = out.resolved_count ? 'Add ' + out.resolved_count + ' Item' + (out.resolved_count === 1 ? '' : 's') : 'Add Items';
            // Bind one-click alias saves on failed lines: save then re-preview.
            bulkResult.querySelectorAll('[data-pesqp-map-save]').forEach(function (btn) {
              btn.addEventListener('click', function () {
                var fi = btn.getAttribute('data-pesqp-map-save');
                var ourSku = bulkResult.querySelector('[data-pesqp-map-sku="' + fi + '"]').value.trim();
                if (!ourSku) return window.alert('Enter our SKU this part number should map to.');
                btn.disabled = true;
                self.api('POST', '/aliases', { email: self.email(), customer_sku: btn.getAttribute('data-csku'), our_sku: ourSku })
                  .then(function () {
                    btn.disabled = false;
                    self.root.querySelector('[data-pesqp-bulk-preview]').click(); // re-resolve with the new alias
                  })
                  .catch(function (e) { btn.disabled = false; window.alert(e.message); });
              });
            });
          })
          .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
      });
      bulkConfirm.addEventListener('click', function () {
        var text = bulkText.value;
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/lines/bulk', { email: self.email(), text: text, confirm: true })
          .then(function (out) {
            self.classList.remove('pesqp-busy');
            bulkWrap.hidden = true;
            bulkText.value = '';
            bulkResult.innerHTML = '';
            bulkConfirm.disabled = true;
            self.paintDetail(out.quote);
            if (out.failed_count > 0) {
              window.alert('Added ' + out.added_count + ' new + updated ' + out.updated_count + ' line(s). ' +
                out.failed_count + ' line(s) failed (see reasons in the preview): ' +
                out.failed.map(function (f) { return (f.sku || ('line ' + f.line)) + ' — ' + f.reason; }).join('; '));
            }
          })
          .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
      });

      // Wave-2B (#109): Your Part Numbers alias manager (list / add / remove).
      var aliasesWrap = this.root.querySelector('[data-pesqp-aliases]');
      var aliasList = this.root.querySelector('[data-pesqp-alias-list]');
      function paintAliases() {
        self.api('GET', '/aliases?email=' + encodeURIComponent(self.email()))
          .then(function (out) {
            var rows = (out.aliases || []).map(function (a) {
              return '<tr><td><strong>' + esc(a.customer_sku) + '</strong></td>' +
                '<td>' + esc(a.our_sku) + '</td>' +
                '<td><button type="button" class="pesqp-remove" data-pesqp-alias-del="' + esc(a.customer_sku) + '">Remove</button></td></tr>';
            }).join('');
            aliasList.innerHTML = rows
              ? '<table class="pesqp-table"><thead><tr><th>Your part #</th><th>Our SKU</th><th></th></tr></thead><tbody>' + rows + '</tbody></table>'
              : '<p class="pesqp-muted">No part numbers saved yet &mdash; add one below, or save one straight from a failed Bulk Add line.</p>';
            aliasList.querySelectorAll('[data-pesqp-alias-del]').forEach(function (btn) {
              btn.addEventListener('click', function () {
                self.api('POST', '/aliases/remove', { email: self.email(), customer_sku: btn.getAttribute('data-pesqp-alias-del') })
                  .then(paintAliases)
                  .catch(function (e) { window.alert(e.message); });
              });
            });
          })
          .catch(function () { aliasList.innerHTML = '<p class="pesqp-muted">Could not load your part numbers right now.</p>'; });
      }
      this.root.querySelector('[data-pesqp-aliases-open]').addEventListener('click', function () {
        aliasesWrap.hidden = !aliasesWrap.hidden;
        if (!aliasesWrap.hidden) paintAliases();
      });
      this.root.querySelector('[data-pesqp-alias-save]').addEventListener('click', function () {
        var csku = self.root.querySelector('[data-pesqp-alias-csku]').value.trim();
        var osku = self.root.querySelector('[data-pesqp-alias-osku]').value.trim();
        if (!csku || !osku) return window.alert('Enter both your part number and our SKU.');
        self.api('POST', '/aliases', { email: self.email(), customer_sku: csku, our_sku: osku })
          .then(function () {
            self.root.querySelector('[data-pesqp-alias-csku]').value = '';
            self.root.querySelector('[data-pesqp-alias-osku]').value = '';
            paintAliases();
          })
          .catch(function (e) { window.alert(e.message); });
      });

      this.root.querySelector('[data-pesqp-rename]').addEventListener('click', function () {
        var name = window.prompt('Quote name:', q.name);
        if (!name || !name.trim() || name.trim() === q.name) return;
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/rename', { email: self.email(), name: name.trim() })
          .then(function (fresh) { self.classList.remove('pesqp-busy'); self.paintDetail(fresh); })
          .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
      });

      this.root.querySelector('[data-pesqp-convert]').addEventListener('click', function () { self.convert(q); });
      this.root.querySelector('[data-pesqp-share]').addEventListener('click', function () { self.openShareDialog(q); });
      this.root.querySelector('[data-pesqp-delete]').addEventListener('click', function () { self.openDeleteDialog(q); });
    }

    /* ---------------- Make a Copy (Wave-1) ---------------- */

    copyQuote(q) {
      var self = this;
      if (!window.confirm('Copy "' + q.name + '" into a new quote? All items, the job name, and notes carry over; the new quote gets its own quote # and a fresh 7-day hold. This quote stays unchanged.')) return;
      this.classList.add('pesqp-busy');
      this.api('POST', '/' + q.id + '/copy', { email: this.email() })
        .then(function (out) {
          self.classList.remove('pesqp-busy');
          window.history.replaceState(null, '', window.location.pathname + '?id=' + encodeURIComponent(out.quote.id));
          self.paintDetail(out.quote);
        })
        .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
    }

    /* ---------------- convert + drift interstitial ---------------- */

    convert(q) {
      var self = this;
      this.classList.add('pesqp-busy');
      this.api('POST', '/' + q.id + '/convert', { email: this.email() })
        .then(function (out) {
          self.classList.remove('pesqp-busy');
          // Wave-2B owner ruling 2026-10-08: conversion is NEVER gated — no
          // pending_approval branch exists anymore; flagged quotes convert
          // like any other (staff are notified internally).
          if (out.requires_review) return self.openDriftDialog(out);
          window.location.href = out.permalink;
        })
        .catch(function (e) { self.classList.remove('pesqp-busy'); window.alert(e.message); });
    }

    /* ---------------- pricing education modal ---------------- */

    openPricingDialog(q) {
      var dlg = document.createElement('dialog');
      dlg.className = 'pesqp-dialog';
      var volumeLine = (q.volume_progress && q.volume_progress.eligible)
        ? '<p><strong>Volume pricing.</strong> This quote is eligible for volume consideration once it reaches the ' +
          money(q.volume_progress.threshold) + ' threshold. Volume pricing is confirmed when the quote is finalized &mdash; ' +
          'the progress bar is an indicator, not an applied discount.</p>'
        : '<p><strong>Volume pricing.</strong> Quotes over the volume threshold may qualify for volume pricing, ' +
          'confirmed when the quote is finalized. Quotes on contract pricing are already at your best price &mdash; ' +
          'contract pricing and volume pricing never combine; we always apply the better single lane.</p>';
      dlg.innerHTML =
        '<h3>How quote pricing works</h3>' +
        '<p><strong>Prices are held for 7 days.</strong> Quoted prices on eligible items are held until ' +
          esc(q.expiry || 'the expiry date shown') + '. Making any change restates this quote and restarts the ' +
          '7-day hold &mdash; your quote number stays the same.</p>' +
        '<p><strong>Availability is confirmed at order time.</strong> A quote never reserves stock and availability ' +
          'is not guaranteed until your order is confirmed. No payment is taken at quote time: at checkout your card ' +
          'is authorized but not charged until we confirm your order. Taxes and delivery fees are calculated at checkout; ' +
          'freight items are quoted separately.</p>' +
        volumeLine +
        '<p><strong>Sharing with price control.</strong> When you share this quote you choose what your client sees: ' +
          'Price and Discounts, Price Only, or No Price (names and quantities only). The same choice applies to the ' +
          'PDF download. Links can be revoked anytime.</p>' +
        '<p class="pesqp-muted">Still stuck? Write to <a href="mailto:sales@portlandiaelectric.supply">sales@portlandiaelectric.supply</a> ' +
          'and reference ' + esc(q.quote_no || 'your quote number') + '.</p>' +
        '<button type="button" class="button button--primary button--full-width" data-close>Got it</button>';
      document.body.appendChild(dlg);
      dlg.querySelector('[data-close]').addEventListener('click', function () { dlg.close(); });
      dlg.addEventListener('close', function () { dlg.remove(); });
      dlg.showModal();
    }

    openDriftDialog(out) {
      var dlg = document.createElement('dialog');
      dlg.className = 'pesqp-dialog';
      var rows = out.drift.map(function (d) {
        return '<tr><td>' + esc(d.title) + '</td><td>' + money(d.quoted) + '</td><td>' + money(d.current) + '</td></tr>';
      }).join('');
      dlg.innerHTML =
        '<h3>Prices have changed since this quote</h3>' +
        '<p class="pesqp-muted">Review the new prices before continuing to checkout.</p>' +
        '<table class="pesqp-table"><thead><tr><th>Item</th><th>Quoted</th><th>Current</th></tr></thead><tbody>' + rows + '</tbody></table>' +
        '<button type="button" class="button button--primary button--full-width" data-go>Continue at Current Prices</button>' +
        '<button type="button" class="button button--secondary button--full-width" data-cancel>Back to Quote</button>';
      document.body.appendChild(dlg);
      dlg.querySelector('[data-go]').addEventListener('click', function () { window.location.href = out.permalink; });
      dlg.querySelector('[data-cancel]').addEventListener('click', function () { dlg.close(); dlg.remove(); });
      dlg.addEventListener('close', function () { dlg.remove(); });
      dlg.showModal();
    }

    /* ---------------- delete (cannot undo) ---------------- */

    openDeleteDialog(q) {
      var self = this;
      var dlg = document.createElement('dialog');
      dlg.className = 'pesqp-dialog';
      dlg.innerHTML =
        '<h3>Delete this quote?</h3>' +
        '<p class="pesqp-muted"><strong>' + esc(q.name) + '</strong> (' + esc(q.quote_no) +
          ') will be permanently deleted. <strong>You cannot undo this action.</strong></p>' +
        '<button type="button" class="button button--primary button--full-width" data-yes>Delete Quote</button>' +
        '<button type="button" class="button button--secondary button--full-width" data-no>Keep Quote</button>';
      document.body.appendChild(dlg);
      dlg.querySelector('[data-no]').addEventListener('click', function () { dlg.close(); });
      dlg.querySelector('[data-yes]').addEventListener('click', function () {
        self.classList.add('pesqp-busy');
        self.api('POST', '/' + q.id + '/delete', { email: self.email(), confirm: true })
          .then(function () {
            self.classList.remove('pesqp-busy');
            dlg.close();
            window.history.replaceState(null, '', window.location.pathname);
            self.renderList();
          })
          .catch(function (e) {
            self.classList.remove('pesqp-busy');
            dlg.close();
            window.alert(e.message);
          });
      });
      dlg.addEventListener('close', function () { dlg.remove(); });
      dlg.showModal();
    }

    /* ---------------- share with price masking ---------------- */

    openShareDialog(q) {
      var self = this;
      var dlg = document.createElement('dialog');
      dlg.className = 'pesqp-dialog';
      dlg.innerHTML =
        '<h3>Share this quote</h3>' +
        '<p class="pesqp-muted">Anyone with the link can view the quote &mdash; no sign-in needed. Choose what prices they see:</p>' +
        '<label class="pesqp-share-mode"><input type="radio" name="pesqp-mode" value="full" checked>' +
          '<strong>Price and Discounts</strong><span>Quoted prices, per-line savings, and totals.</span></label>' +
        '<label class="pesqp-share-mode"><input type="radio" name="pesqp-mode" value="price_only">' +
          '<strong>Price Only</strong><span>Retail unit prices and totals &mdash; no discounts or savings shown.</span></label>' +
        '<label class="pesqp-share-mode"><input type="radio" name="pesqp-mode" value="none">' +
          '<strong>No Price</strong><span>Product names and quantities only.</span></label>' +
        '<input class="field__input" type="text" maxlength="500" placeholder="Note for your client (optional)" data-pesqp-note style="width:100%;box-sizing:border-box;margin:8px 0 12px">' +
        '<button type="button" class="button button--secondary button--full-width" data-preview style="margin-bottom:10px">Preview as Client</button>' +
        '<button type="button" class="button button--primary button--full-width" data-gen>Generate Share Link</button>' +
        '<div data-pesqp-result></div>' +
        '<button type="button" class="button button--secondary button--full-width" data-close style="margin-top:10px">Close</button>';
      document.body.appendChild(dlg);

      // Preview as client (Wave-1, P2-9 BEAT): opens the exact recipient view
      // with the chosen masking mode + note, via an ephemeral 15-minute
      // preview token. Never creates, replaces, or revokes a real share link.
      dlg.querySelector('[data-preview]').addEventListener('click', function () {
        var mode = dlg.querySelector('input[name="pesqp-mode"]:checked').value;
        var note = dlg.querySelector('[data-pesqp-note]').value.trim();
        var btn = dlg.querySelector('[data-preview]');
        btn.disabled = true;
        self.api('POST', '/' + q.id + '/preview', { email: self.email(), mode: mode, note: note || undefined })
          .then(function (out) {
            btn.disabled = false;
            window.open(out.preview_path, '_blank', 'noopener');
            dlg.querySelector('[data-pesqp-result]').innerHTML =
              '<p class="pesqp-muted" style="font-size:0.78rem;margin-top:8px">Preview opened in a new tab &mdash; that is exactly what your client will see. ' +
              'The preview link expires in ' + esc(out.ttl_minutes) + ' minutes and does not affect any share links you already sent.</p>';
          })
          .catch(function (e) {
            btn.disabled = false;
            window.alert(e.message);
          });
      });

      dlg.querySelector('[data-gen]').addEventListener('click', function () {
        var mode = dlg.querySelector('input[name="pesqp-mode"]:checked').value;
        var note = dlg.querySelector('[data-pesqp-note]').value.trim();
        var genBtn = dlg.querySelector('[data-gen]');
        genBtn.disabled = true;
        self.api('POST', '/' + q.id + '/share', { email: self.email(), mode: mode, note: note || undefined })
          .then(function (out) {
            genBtn.disabled = false;
            var url = window.location.origin + out.share_path;
            dlg.querySelector('[data-pesqp-result]').innerHTML =
              '<div class="pesqp-share-link">' +
                '<input type="text" readonly value="' + esc(url) + '" data-pesqp-link aria-label="Share link">' +
                '<button type="button" class="button button--secondary" data-pesqp-copy>Copy</button>' +
              '</div>' +
              '<p class="pesqp-muted" style="font-size:0.78rem;margin-top:8px">Generating a new link or revoking disables all previous links for this quote.</p>' +
              '<button type="button" class="pesqp-remove" data-pesqp-revoke>Revoke all share links for this quote</button>';
            var linkInput = dlg.querySelector('[data-pesqp-link]');
            dlg.querySelector('[data-pesqp-copy]').addEventListener('click', function () {
              linkInput.select();
              var done = function () { dlg.querySelector('[data-pesqp-copy]').textContent = 'Copied'; };
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(linkInput.value).then(done, function () { document.execCommand('copy'); done(); });
              } else {
                document.execCommand('copy');
                done();
              }
            });
            dlg.querySelector('[data-pesqp-revoke]').addEventListener('click', function () {
              self.api('POST', '/' + q.id + '/share/revoke', { email: self.email() })
                .then(function (r) {
                  dlg.querySelector('[data-pesqp-result]').innerHTML =
                    '<p class="pesqp-muted">Revoked ' + r.revoked + ' link(s). They no longer work.</p>';
                })
                .catch(function (e) { window.alert(e.message); });
            });
          })
          .catch(function (e) {
            genBtn.disabled = false;
            window.alert(e.message);
          });
      });
      dlg.querySelector('[data-close]').addEventListener('click', function () { dlg.close(); });
      dlg.addEventListener('close', function () { dlg.remove(); });
      dlg.showModal();
    }

    /* ---------------- Wave-2A: job-scoped reorder (P2-11 BEAT) ----------------
     * History: GET {ep}/reorder/history?email&q -> converted quotes + past
     * orders, searchable by quote name / PO / job name / order #.
     * Reorder: POST {ep}/reorder {email, source:{type,ref}} ->
     *   mode "quote": new quote at the ORIGINAL prices (price memory) — open it;
     *   mode "cart":  current-price cart permalink + visible prices-updated
     *                 notice (never a silent reprice).
     */

    bindReorder() {
      var self = this;
      var q = this.root.querySelector('[data-pesqp-reorder-q]');
      var go = this.root.querySelector('[data-pesqp-reorder-go]');
      if (!q || !go) return;
      var run = function () { self.loadReorderHistory(q.value); };
      go.addEventListener('click', run);
      q.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); run(); } });
      this.loadReorderHistory('');
    }

    loadReorderHistory(q) {
      var self = this;
      var out = this.root.querySelector('[data-pesqp-reorder-out]');
      if (!out) return;
      out.innerHTML = '<p class="pesqp-muted">Searching&hellip;</p>';
      this.api('GET', '/reorder/history?email=' + encodeURIComponent(this.email()) + '&q=' + encodeURIComponent(q || ''))
        .then(function (data) { self.paintReorderResults(data); })
        .catch(function () {
          out.innerHTML = '<p class="pesqp-muted">Past jobs are temporarily unavailable &mdash; your quotes above are unaffected.</p>';
        });
    }

    paintReorderResults(data) {
      var self = this;
      var out = this.root.querySelector('[data-pesqp-reorder-out]');
      var rows = (data && data.results) || [];
      if (!rows.length) {
        out.innerHTML = '<p class="pesqp-muted">No past jobs found. Convert a quote or place an order and it will appear here for one-tap reorder.</p>';
        return;
      }
      out.innerHTML = rows.map(function (r) {
        var badge = r.kind === 'quote'
          ? '<span class="pesqp-badge pesqp-badge--active">Quote</span>'
          : '<span class="pesqp-badge pesqp-badge--order">Order</span>';
        return (
          '<div class="pesqp-reorder-row">' +
            '<div class="pesqp-reorder-row__info">' +
              '<span class="pesqp-card__name">' + esc(r.name) + ' ' + badge + '</span>' +
              '<span class="pesqp-card__meta">' + esc(r.sub || '') +
                (r.when ? ' &middot; ' + esc(dateOnly(r.when)) : '') +
                ' &middot; ' + (r.line_count || 0) + ' item' + (r.line_count === 1 ? '' : 's') +
                (r.total != null ? ' &middot; ' + money(r.total) : '') + '</span>' +
            '</div>' +
            '<button type="button" class="button button--secondary" data-pesqp-reorder-run="' + esc(r.kind) + '|' + esc(String(r.ref)) + '">Reorder</button>' +
          '</div>'
        );
      }).join('');
      out.querySelectorAll('[data-pesqp-reorder-run]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var parts = btn.getAttribute('data-pesqp-reorder-run').split('|');
          self.runReorder(parts[0], parts.slice(1).join('|'), btn);
        });
      });
    }

    runReorder(kind, ref, btn) {
      var self = this;
      btn.disabled = true;
      btn.textContent = 'Reordering\u2026';
      this.api('POST', '/reorder', { email: this.email(), source: { type: kind, ref: ref } })
        .then(function (out) {
          btn.disabled = false;
          btn.textContent = 'Reorder';
          if (out.mode === 'quote') {
            // Honored original pricing: open the fresh quote (review + checkout there).
            window.history.replaceState(null, '', window.location.pathname + '?id=' + encodeURIComponent(out.quote.id));
            self.renderDetail(out.quote.id);
            window.scrollTo(0, 0);
            return;
          }
          // Current-price fallback: visible notice, never a silent reprice.
          var box = self.root.querySelector('[data-pesqp-reorder-out]');
          var missing = (out.missing || []).map(function (m) {
            return '<li>' + esc(m.title || m.sku || 'item') + ' &mdash; ' + esc(m.reason || 'unavailable') + '</li>';
          }).join('');
          box.insertAdjacentHTML('afterbegin',
            '<div class="pesqp-banner pesqp-banner--warn pesqp-reorder-notice">' +
              '<strong>Prices updated.</strong> ' + esc(out.notice || 'Some items changed — this cart uses current prices.') +
              (missing ? '<ul class="pesqp-reorder-missing">' + missing + '</ul>' : '') +
              '<p style="margin:10px 0 0"><a class="button button--primary" href="' + esc(out.permalink) + '">Review cart at current prices</a></p>' +
            '</div>');
        })
        .catch(function (e) {
          btn.disabled = false;
          btn.textContent = 'Reorder';
          window.alert(e.message);
        });
    }

    /* ---------------- degraded ---------------- */

    renderDegraded(e) {
      var self = this;
      this.root.innerHTML =
        '<div class="pesqp-head"><h1 class="pesqp-title">My Quotes</h1></div>' +
        '<p class="pesqp-muted">Live pricing is temporarily unavailable. Your saved quotes are safe &mdash; please try again shortly.</p>' +
        '<button type="button" class="button button--secondary" data-pesqp-retry>Try Again</button>' +
        '<p class="pesqp-error">' + esc(e && e.message ? e.message : '') + '</p>';
      this.root.querySelector('[data-pesqp-retry]').addEventListener('click', function () { self.renderList(); });
    }
  }

  if (!customElements.get('pes-quote-pages')) {
    customElements.define('pes-quote-pages', PesQuotePages);
  }
})();
